<template>
  <main class="page">
    <header class="top">
      <div></div>
      <button class="top__wechat" type="button" aria-label="微信"></button>
    </header>
    <section class="hero" aria-label="头图">
      <div class="hero__title">
        <img class="hero__titleImg" src="@/assets/img/title.png" alt="智能AI问答系统" />
        <img class="hero__subtitle" src="@/assets/img/tips.png" alt="智能AI问答系统" />
      </div>
    </section>
    <div class="intro">
      <img src="@/assets/img/robot.png" alt="机器人" />
      <div class="intro__text">Hi~我是小助手，很高兴为您服务！</div>
    </div>
    <section class="chat" aria-label="对话区域">
      <div class="chat__scroll" ref="chatScroll" role="log" aria-live="polite" @scroll="updateFades">
        <section class="card card--guess" aria-label="猜你想问">
          <div class="guess__header">
            <div class="guess__left">
              <img class="guess__icon" src="@/assets/img/question.png" alt="" />
            </div>
          </div>
          <div class="guess__list">
            <button v-for="(q, index) in guessQuestions" :key="index" type="button" class="guess__item"
              @click="selectGuess(q)">
              {{ q }}
            </button>
          </div>
        </section>
        <div v-for="(msg, index) in messages" :key="index" :class="['bubbleRow', `bubbleRow--${msg.role}`]">
          <div :class="['bubble', `bubble--${msg.role}`]">
            <div v-if="msg.role === 'bot'" class="bubble__md" v-html="renderMarkdown(msg.content)"></div>
            <div v-else class="bubble__text">{{ msg.content }}</div>
          </div>
        </div>
        <div v-if="isThinking || currentAnswer" class="bubbleRow bubbleRow--bot">
          <div v-if="!currentAnswer" class="thinking">
            <div class="thinking__left">
              <span>{{ thinkingText }}</span>
            </div>
            <div class="thinking__right">
              <button class="thinking__stop" type="button" @click="stopStreaming">
                <img src="@/assets/img/stop.png" alt="" />
                <span>停止回答</span>
              </button>
            </div>
          </div>
          <div v-else class="bubble bubble--bot">
            <div class="bubble__md" v-html="renderMarkdown(currentAnswer)"></div>
            <div class="bubble__stop__wrapper">
              <button class="bubble__stop" type="button" @click="stopStreaming">
                <img src="@/assets/img/stop.png" alt="" />
                <span>停止回答</span>
              </button>
            </div>
          </div>
        </div>
      </div>
      <!-- 上下虚化遮罩（不参与滚动） -->
      <div
        :class="['chat__fade', 'chat__fade--top', { 'chat__fade--visible': showTopFade }]"
        aria-hidden="true"
      ></div>
      <div
        :class="['chat__fade', 'chat__fade--bottom', { 'chat__fade--visible': showBottomFade }]"
        aria-hidden="true"
      ></div>
    </section>
    <footer class="footer" aria-label="输入区">
      <button class="footer__clear" type="button" @click="clearChat">
        <img src="@/assets/img/clear.png" alt="" />
        <span>清除对话</span>
      </button>
      <div class="composer">
        <textarea
          class="composer__input"
          ref="composerInput"
          v-model="inputText"
          placeholder="在这里输入您的问题"
          maxlength="200"
          autocomplete="off"
          rows="1"
          @keydown="onComposerKeydown"
          @input="autoResizeComposer"
        />
        <button
          class="composer__send"
          type="button"
          :aria-label="isStreaming ? '停止回答' : '发送'"
          @click="onSendClick"
        >
          <img :src="isStreaming ? noSendIcon : sendIcon" alt="" />
        </button>
      </div>
    </footer>
    <div class="sheet" v-if="showSheet" @click.self="closeSheet">
      <div class="sheet__mask" @click="closeSheet"></div>
      <div class="sheet__panel" role="dialog" aria-modal="true" aria-label="提示">
        <div class="sheet__title">提示</div>
        <div class="sheet__desc">{{ sheetMessage }}</div>
        <div class="sheet__actions">
          <button class="sheet__btn sheet__btn--primary" type="button" @click="closeSheet">
            我知道了
          </button>
        </div>
      </div>
    </div>
  </main>
</template>

<script>
import { marked } from "marked";
import DOMPurify from "dompurify";

const CONFIG = {
  fastgpt: {
    baseUrl: "/api",
    appId: "67aeaf083af0e4ef307e3144",
    key: "bp2DeaZ5B6OHB51vnZSA0Zt1YN5spXQoglRCHZlUtv21uH5HEuu5sCoqW",
  },
};

import sendIcon from "@/assets/img/send.png";
import noSendIcon from "@/assets/img/noSend.png";

export default {
  name: "ChatPage",
  data() {
    return {
      messages: [],
      inputText: "",
      guessQuestions: [], // 从 API 获取的猜你想问问题列表
      currentChatId: null,
      isThinking: false,
      thinkingText: "努力思考中...",
      thinkingTimer: null,
      abortController: null,
      currentAnswer: "",
      showSheet: false,
      sheetMessage: "",
      showTopFade: false,
      showBottomFade: false,
      sendIcon,
      noSendIcon,
    };
  },
  computed: {
    isStreaming() {
      return !!this.abortController;
    },
  },
  mounted() {
    marked.setOptions({ breaks: true });
    this.init();
    this.$nextTick(() => {
      this.updateFades();
      this.autoResizeComposer();
    });
    window.addEventListener("resize", this.updateFades);
  },
  beforeDestroy() {
    this.removeThinking();
    if (this.abortController) {
      this.abortController.abort();
    }
    window.removeEventListener("resize", this.updateFades);
  },
  methods: {
    renderMarkdown(content) {
      const md = String(content || "");
      const html = marked.parse(md);
      return DOMPurify.sanitize(html, { USE_PROFILES: { html: true } });
    },
    onSendClick() {
      if (this.isStreaming) {
        this.stopStreaming();
        return;
      }
      this.sendMessage();
    },
    onComposerKeydown(e) {
      if (e.key !== "Enter") return;
      // 手机上回车默认换行；PC 端用 Ctrl/⌘ + Enter 发送
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        this.sendMessage();
      }
    },
    autoResizeComposer() {
      this.$nextTick(() => {
        const el = this.$refs.composerInput;
        if (!el) return;
        // 重置高度再计算，避免越撑越高
        el.style.height = "auto";
        const maxHeight = 120; // 约 4-5 行
        el.style.height = Math.min(el.scrollHeight, maxHeight) + "px";
      });
    },
    init() {
      if (window.location.protocol !== "file:") {
        this.initChat();
      }
    },
    genChatId() {
      return Math.floor(Math.random() * 1000000)
        .toString()
        .padStart(6, "0");
    },
    getAuthHeaders() {
      return {
        Authorization: "Bearer fastgpt-" + CONFIG.fastgpt.key,
        "Content-Type": "application/json",
      };
    },
    scrollToBottom() {
      this.$nextTick(() => {
        if (this.$refs.chatScroll) {
          this.$refs.chatScroll.scrollTop = this.$refs.chatScroll.scrollHeight;
        }
        this.updateFades();
      });
    },
    updateFades() {
      const el = this.$refs.chatScroll;
      if (!el) return;

      const scrollTop = el.scrollTop || 0;
      const clientHeight = el.clientHeight || 0;
      const scrollHeight = el.scrollHeight || 0;

      // 只有在内容确实可滚动时才显示虚化
      const canScroll = scrollHeight - clientHeight > 2;
      if (!canScroll) {
        this.showTopFade = false;
        this.showBottomFade = false;
        return;
      }

      // 离顶部/底部有距离才淡入（更自然）
      this.showTopFade = scrollTop > 6;
      this.showBottomFade = scrollTop + clientHeight < scrollHeight - 6;
    },
    selectGuess(question) {
      this.inputText = question;
      this.autoResizeComposer();
      this.sendMessage();
    },
    appendBubble(role, text) {
      this.messages.push({ role, content: text });
      this.scrollToBottom();
    },
    appendThinking() {
      this.removeThinking();
      this.isThinking = true;
      this.thinkingText = "努力思考中...";
      let dot = 0;
      this.thinkingTimer = window.setInterval(() => {
        dot = (dot + 1) % 4;
        this.thinkingText = `努力思考中${".".repeat(dot)}${dot === 0 ? "..." : ""}`;
      }, 450);
      this.scrollToBottom();
    },
    removeThinking() {
      if (this.thinkingTimer) {
        clearInterval(this.thinkingTimer);
        this.thinkingTimer = null;
      }
      this.isThinking = false;
    },
    async initChat() {
      const hasMessages = this.messages.length > 0;

      if (!this.currentChatId) {
        this.currentChatId = this.genChatId();
      }

      const url = `${CONFIG.fastgpt.baseUrl}/core/chat/init?chatId=${encodeURIComponent(
        this.currentChatId
      )}&appId=${encodeURIComponent(CONFIG.fastgpt.appId)}`;

      try {
        const res = await fetch(url, { method: "GET", headers: this.getAuthHeaders() });
        if (!res.ok) {
          const errorText = await res.text().catch(() => "");
          throw new Error(`初始化失败: ${res.status} ${res.statusText}${errorText ? ` - ${errorText}` : ""}`);
        }
        const json = await res.json();

        const welcomeText = json?.data?.app?.chatConfig?.welcomeText || "";
        const lines = String(welcomeText)
          .split("\n")
          .map((s) => s.trim())
          .filter(Boolean);

        // 第一行是欢迎语
        if (lines.length > 0 && !hasMessages) {
          this.appendBubble("bot", lines[0]);
        }

        // 提取 [] 内的内容作为猜你想问的问题
        const qs = lines
          .slice(1) // 跳过第一行欢迎语
          .map((l) => {
            // 匹配 [内容] 格式，提取方括号内的内容
            const match = l.match(/^\[(.+)\]$/);
            return match ? match[1].trim() : null;
          })
          .filter(Boolean); // 过滤掉 null 和空字符串

        // 直接将提取的问题赋值给 guessQuestions
        if (qs.length >= 1) {
          this.guessQuestions = qs;
        } else {
          this.guessQuestions = [];
        }
      } catch (e) {
        console.error("初始化聊天失败:", e);
        if (!hasMessages) {
          this.appendBubble("bot", "Hi~我是小助手，很高兴为您服务！");
        }
      }
    },
    stopStreaming() {
      if (this.abortController) {
        this.abortController.abort();
        this.abortController = null;
      }
      if (this.isThinking) {
        this.removeThinking();
        this.appendBubble("bot", "已停止。");
      }
      // 如果已经开始返回内容，停止时把当前内容落盘为一条消息
      if (this.currentAnswer) {
        this.appendBubble("bot", this.currentAnswer);
        this.currentAnswer = "";
      }
    },
    async sendMessage() {
      // 正在回答时不允许再次发送（按钮会变成“停止”）
      if (this.isStreaming) return;

      const text = this.inputText.trim();
      if (!text) return;

      if (!this.currentChatId) {
        this.currentChatId = this.genChatId();
        await this.initChat();
      }

      this.inputText = "";
      this.autoResizeComposer();
      this.appendBubble("me", text);
      this.appendThinking();

      this.abortController = new AbortController();
      this.currentAnswer = "";

      try {
        const url = `${CONFIG.fastgpt.baseUrl}/v1/chat/completions`;
        const payload = {
          stream: true,
          chatId: this.currentChatId,
          messages: [{ role: "user", content: text }],
        };

        const res = await fetch(url, {
          method: "POST",
          headers: this.getAuthHeaders(),
          body: JSON.stringify(payload),
          signal: this.abortController.signal,
        });

        if (!res.ok) {
          const errorText = await res.text().catch(() => "");
          throw new Error(`聊天请求失败: ${res.status} ${res.statusText}${errorText ? ` - ${errorText}` : ""}`);
        }

        if (!res.body) {
          throw new Error("浏览器不支持流式响应");
        }

        const reader = res.body.getReader();
        const decoder = new TextDecoder("utf-8");
        let fullText = "";
        let buffer = "";

        // eslint-disable-next-line no-constant-condition
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split("\n");
          buffer = lines.pop() || "";

          for (const line of lines) {
            if (!line.trim()) continue;

            if (line.startsWith("data: ")) {
              const dataStr = line.slice(6).trim();

              if (dataStr === "[DONE]") {
                this.abortController = null;
                break;
              }

              try {
                const parsed = JSON.parse(dataStr);
                const delta = parsed?.choices?.[0]?.delta?.content || "";

                if (delta) {
                  fullText += delta;
                  // 当开始接收到内容时，隐藏思考状态，显示答案
                  if (this.isThinking) {
                    this.removeThinking();
                  }
                  this.currentAnswer = fullText;
                  this.scrollToBottom();
                }
              } catch (err) {
                if (dataStr !== "" && !dataStr.startsWith(":")) {
                  console.warn("SSE 解析错误:", err, "原始数据:", dataStr);
                }
              }
            }
          }
        }

        if (buffer.trim()) {
          if (buffer.startsWith("data: ")) {
            const dataStr = buffer.slice(6).trim();
            if (dataStr && dataStr !== "[DONE]") {
              try {
                const parsed = JSON.parse(dataStr);
                const delta = parsed?.choices?.[0]?.delta?.content || "";
                if (delta) {
                  fullText += delta;
                  if (this.isThinking) {
                    this.removeThinking();
                  }
                  this.currentAnswer = fullText;
                  this.scrollToBottom();
                }
              } catch (err) {
                console.warn("处理缓冲区数据时出错:", err);
              }
            }
          }
        }

        // 思考结束后，将答案添加到消息列表，并清空 currentAnswer
        if (this.currentAnswer) {
          this.appendBubble("bot", this.currentAnswer);
          this.currentAnswer = "";
        }
      } catch (e) {
        if (e && e.name === "AbortError") {
          this.removeThinking();
          return;
        }
        console.error("发送消息错误:", e);
        this.removeThinking();
        this.appendBubble("bot", `发生错误：${e.message || "请稍后重试"}`);
      } finally {
        this.abortController = null;
      }
    },
    clearChat() {
      this.stopStreaming();
      this.messages = [];
      this.currentAnswer = "";
      this.guessQuestions = [];
      this.currentChatId = this.genChatId();
      this.initChat();
      this.autoResizeComposer();
    },
    openSheet(message) {
      this.sheetMessage = message;
      this.showSheet = true;
    },
    closeSheet() {
      this.showSheet = false;
    },
  },
};
</script>

<style scoped>
@import '@/assets/css/chat.css';
</style>
